import { COGNITO_CLIENT_ID, COGNITO_USER_POOL_ID } from '../data/environment'

class CognitoError extends Error {
  type?: string

  constructor(message: string, type?: string) {
    super(message)
    this.type = type
    Object.setPrototypeOf(this, CognitoError.prototype)
  }
}

export const initiateAuth = async (phone: string) => {
  const username = phone

  const doInitiateAuth = () =>
    cognitoRequest({
      method: 'POST',
      headers: {
        'x-amz-target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'CUSTOM_AUTH',
        AuthParameters: {
          USERNAME: username,
        },
        ClientId: COGNITO_CLIENT_ID,
        ClientMetadata: {},
      }),
    })

  // Creates the account; if the user exists after all (e.g. disabled), the
  // sign-in error is the meaningful one, not "user already exists".
  const doSignUp = (signInErr: unknown) =>
    cognitoRequest({
      method: 'POST',
      headers: {
        'x-amz-target': 'AWSCognitoIdentityProviderService.SignUp',
      },
      body: JSON.stringify({
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: COGNITO_CLIENT_ID,
        ClientMetadata: {},
        Password: crypto.randomUUID(),
        Username: username,
        UserAttributes: [
          {
            Name: 'phone_number',
            Value: phone,
          },
        ],
      }),
    }).catch((signUpErr: unknown) => {
      const isExistingUser =
        signUpErr instanceof CognitoError &&
        signUpErr.type === 'UsernameExistsException'
      throw isExistingUser ? signInErr : signUpErr
    })

  try {
    return await doInitiateAuth()
  } catch (err) {
    // Cognito rejected the sign-in: the user most likely does not exist yet,
    // so create the account (v1 behaviour). This pool runs a CUSTOM_AUTH flow,
    // and for an unknown number the DefineAuthChallenge lambda fails with
    // UserLambdaValidationException before Cognito ever says "user not
    // found", so the decision cannot hinge on specific error codes. Network
    // failures and non-Cognito responses (no error code) are re-thrown.
    const isCognitoRejection = err instanceof CognitoError && Boolean(err.type)
    if (!isCognitoRejection) throw err

    const response = await doSignUp(err)

    if (response?.UserConfirmed) {
      return doInitiateAuth()
    }

    throw new Error('Invalid phone number')
  }
}

interface ValidateCodeProps {
  username: string
  code: string
  session?: string
}

export const validateCode = async ({
  username,
  code,
  session,
}: ValidateCodeProps) =>
  cognitoRequest({
    method: 'POST',
    headers: {
      'x-amz-target':
        'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
    },
    body: JSON.stringify({
      ClientId: COGNITO_CLIENT_ID,
      ChallengeName: 'CUSTOM_CHALLENGE',
      ChallengeResponses: {
        USERNAME: username,
        ANSWER: code,
      },
      Session: session,
    }),
  })

const cognitoRequest = async (options: RequestInit) => {
  const region = COGNITO_USER_POOL_ID.split('_')[0]

  const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    ...options,
    headers: {
      accept: '*/*',
      'accept-language': 'en-US,en;q=0.9',
      'cache-control': 'max-age=0',
      'content-type': 'application/x-amz-json-1.1',
      'sec-fetch-dest': 'empty',
      'sec-fetch-mode': 'cors',
      'sec-fetch-site': 'cross-site',
      'x-amz-user-agent': 'aws-amplify/5.0.4 js',
      ...(options.headers || {}),
    },
  })

  if (!response.ok) {
    const errBody = await response.text()
    let error: CognitoError

    try {
      const asJson = JSON.parse(errBody)
      // Cognito's error code field is `__type` (e.g. "UserNotFoundException")
      error = new CognitoError(asJson.message, asJson.__type)
    } catch {
      error = new CognitoError(`Cognito error: ${errBody}`)
    }
    throw error
  }

  return response.json()
}
